/**
 * gc-sources — the pure logic behind the one GameChanger import screen.
 *
 * A finished GameChanger game reaches Dugout as up to three files, in any order,
 * by share sheet, file picker or paste:
 *   - the Scorebook PDF   (play-by-play, pitch sequences)
 *   - the Game Stats PDF  (the box score: final score, exact pitch counts / BF)
 *   - the Stats CSV       (full stat lines, field time, rate stats)
 *
 * This module answers the questions the screen has to answer before anything is
 * saved: which game do these files belong to, do they agree with each other, and
 * which pitch count wins. No React, no Firestore, no pdfjs, so it runs under tsx
 * against real exports (scripts/test-gc-import.ts).
 *
 * Why the game match is done on the LOCAL calendar day: calendar-synced games are
 * stored as UTC instants ("2026-09-22T00:30:00.000Z" is 7:30pm Central on the
 * 21st). The old importer compared `date.slice(0, 10)` against the scorebook's
 * "2026-09-21", missed every evening game, and created a duplicate game instead.
 */

import Papa from 'papaparse';
import { format } from 'date-fns';
import { matchPlayers, PlayerMatchResult } from '../xovr-engine';
import { parseGCCsv, ParsedGCPlayer } from '../gcCsvParser';
import type { ReconciledGame, RosterRef } from '../pdf/reconcileGameImport';
import type { ParsedGameStatsSheet, StatsSheetTeam } from '../../types/import.types';

export type SourceKind = 'scorebook' | 'boxScore' | 'csv';

// ── CSV ──────────────────────────────────────────────────────────────────────

/** Parse CSV text (a file's contents or a paste) into GameChanger player rows. */
export function parseCsvText(text: string): ParsedGCPlayer[] {
  const res = Papa.parse(text, { header: false, skipEmptyLines: false });
  return parseGCCsv(res.data as any[][]);
}

/** Does this text look like a GameChanger stats export at all? Used to accept a
 *  CSV that arrives without a .csv name (paste, odd MIME types from Files). */
export function looksLikeGcCsv(text: string): boolean {
  // Comma for an exported file, tab for GameChanger's copy-to-clipboard.
  return /(^|\n)\s*"?Number"?\s*[,\t]\s*"?Last"?\s*[,\t]\s*"?First"?/.test(text.slice(0, 20000));
}

/** How many games the export covers. GameChanger's GP column is games played, so
 *  a one-game export reads 1 for everyone who played; a season export does not. */
export function csvGameCount(rows: ParsedGCPlayer[]): number {
  return rows.reduce((m, r) => Math.max(m, r.stats?.gp ?? 0), 0);
}

/** Players who actually appeared (a row with no stats is a DNP). */
export function csvPlayedRows(rows: ParsedGCPlayer[]): number {
  return rows.filter(r => r.stats).length;
}

/** Our runs, from the CSV's batting R column. */
export function csvRunsScored(rows: ParsedGCPlayer[]): number {
  return rows.reduce((s, r) => s + (r.stats?.r ?? 0), 0);
}

// ── Dates and names ──────────────────────────────────────────────────────────

/** A game's calendar day in the coach's own timezone, as yyyy-MM-dd. Accepts a
 *  bare day, a local wall time ("2026-09-21T18:00") or a UTC instant. */
export function localDay(d: string | undefined | null): string {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d).slice(0, 10);
  return format(dt, 'yyyy-MM-dd');
}

function dayDiff(a: string, b: string): number {
  const ta = Date.parse(`${a}T12:00:00`);
  const tb = Date.parse(`${b}T12:00:00`);
  if (isNaN(ta) || isNaN(tb)) return Infinity;
  return Math.round((ta - tb) / 86400000);
}

const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Words that every youth team name shares, so overlapping on them proves nothing.
const GENERIC_WORDS = new Set([
  'baseball', 'softball', 'club', 'team', 'academy', 'select', 'elite', 'travel',
  'the', 'and', 'little', 'league', 'youth', 'national', 'premier',
]);
const nameWords = (s: string) =>
  (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
    .filter(w => w.length >= 3 && !GENERIC_WORDS.has(w) && !/^\d+u?$/.test(w));

/** Are these two opponent names the same team? GameChanger, the calendar feed and
 *  the coach all spell names a little differently ("KC Diablos 10U" vs "Diablos"). */
export function opponentsMatch(a: string | undefined, b: string | undefined): boolean {
  const na = norm(a || '');
  const nb = norm(b || '');
  if (!na || !nb) return false;
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  const wb = new Set(nameWords(b || ''));
  return nameWords(a || '').some(w => wb.has(w));
}

// ── Which team is ours on the box score ──────────────────────────────────────

/** Pick the coach's side of the Game Stats PDF: by name first, then by roster
 *  jersey overlap (Dugout's team name rarely matches GameChanger's). Mirrors
 *  applyStatsSheet in reconcileGameImport so the two can't disagree. */
export function ourSideOfSheet(sheet: ParsedGameStatsSheet, coachTeamName: string, roster: RosterRef[]): { ours?: StatsSheetTeam; theirs?: StatsSheetTeam } {
  const target = norm(coachTeamName);
  let ours = sheet.teams.find(t => { const n = norm(t.teamName); return !!target && !!n && (n.includes(target) || target.includes(n)); });
  if (!ours) {
    const nums = new Set(roster.map(r => String(r.number).trim()));
    let best = 0;
    for (const t of sheet.teams) {
      const overlap = t.batters.filter(b => nums.has(String(b.number).trim())).length;
      if (overlap > best) { best = overlap; ours = t; }
    }
    if (best === 0) ours = undefined;
  }
  return { ours, theirs: ours ? sheet.teams.find(t => t !== ours) : undefined };
}

// ── The game these files describe ────────────────────────────────────────────

export interface GameIdentity {
  date?: string;              // yyyy-MM-dd as printed on the PDF
  opponent?: string;
  ourTeamName?: string;
  homeOrAway?: 'home' | 'away';
  final?: { us: number; them: number };
}

/** Everything the PDFs say about which game this is. The scorebook (via reconcile)
 *  is preferred for names; the box score adds the final score. */
export function identifyGame(
  recon: ReconciledGame | null,
  sheet: ParsedGameStatsSheet | null,
  coachTeamName: string,
  roster: RosterRef[],
): GameIdentity {
  const id: GameIdentity = {};
  if (recon) {
    id.date = recon.date;
    id.opponent = recon.opponent;
    id.ourTeamName = recon.ourTeamName;
    id.homeOrAway = recon.homeOrAway;
  }
  if (sheet) {
    const { ours, theirs } = ourSideOfSheet(sheet, coachTeamName, roster);
    if (!id.date) id.date = sheet.date;
    if (ours) {
      if (!id.opponent && theirs) id.opponent = theirs.teamName;
      if (!id.ourTeamName) id.ourTeamName = ours.teamName;
      if (!id.homeOrAway) id.homeOrAway = ours.side;
      if (sheet.finalScore) {
        const side = id.homeOrAway ?? ours.side;
        id.final = side === 'home'
          ? { us: sheet.finalScore.home, them: sheet.finalScore.away }
          : { us: sheet.finalScore.away, them: sheet.finalScore.home };
      }
    }
  }
  return id;
}

/** Do the two PDFs describe the same game? Returns a coach-facing reason when not. */
export function pdfsDisagree(recon: ReconciledGame | null, sheet: ParsedGameStatsSheet | null): string | null {
  if (!recon || !sheet) return null;
  if (recon.date && sheet.date && recon.date !== sheet.date) {
    return `The scorebook is from ${prettyDay(recon.date)} but the game stats PDF is from ${prettyDay(sheet.date)}.`;
  }
  const sheetNames = [sheet.homeTeam, sheet.awayTeam].filter(Boolean) as string[];
  if (recon.opponent && sheetNames.length && !sheetNames.some(n => opponentsMatch(n, recon.opponent))) {
    return `The scorebook is against ${recon.opponent} but the game stats PDF is ${sheet.awayTeam || '?'} at ${sheet.homeTeam || '?'}.`;
  }
  return null;
}

export function prettyDay(day: string | undefined): string {
  if (!day) return '';
  const t = Date.parse(`${day}T12:00:00`);
  return isNaN(t) ? day : format(new Date(t), 'EEE, MMM d');
}

export interface GameMatch {
  game: any;
  /** exact = same day + same opponent. likely = one step looser (adjacent day,
   *  or the only game that day). The screen shows both; it asks on neither. */
  confidence: 'exact' | 'likely';
  /** Another game also fits (a doubleheader vs the same team). */
  ambiguous: boolean;
}

const hasImportedData = (g: any) =>
  !!(g.gcImport || (g.stats && Object.keys(g.stats).length) || g.importedFrom);

/** Find the scheduled game these files belong to. Never creates anything. */
export function findGameMatch(identity: GameIdentity, games: any[]): GameMatch | null {
  if (!identity.date) return null;
  const pool = games.filter(g => g && !g.isPractice && g.date);
  const scoreFits = (g: any) => !identity.final || !g.result
    || (g.result.myScore === identity.final.us && g.result.opponentScore === identity.final.them);

  // Doubleheader tie-break: the game whose score agrees, then one not yet imported.
  const best = (list: any[]) => [...list].sort((a, b) =>
    Number(scoreFits(b)) - Number(scoreFits(a))
    || Number(hasImportedData(a)) - Number(hasImportedData(b))
    || String(a.date).localeCompare(String(b.date)))[0];

  const sameDay = pool.filter(g => localDay(g.date) === identity.date);
  const strong = sameDay.filter(g => opponentsMatch(g.opponent, identity.opponent));
  if (strong.length) return { game: best(strong), confidence: 'exact', ambiguous: strong.length > 1 };

  const near = pool.filter(g => Math.abs(dayDiff(localDay(g.date), identity.date!)) === 1 && opponentsMatch(g.opponent, identity.opponent));
  if (near.length === 1) return { game: near[0], confidence: 'likely', ambiguous: false };

  if (sameDay.length === 1 && (!identity.opponent || !sameDay[0].opponent)) {
    return { game: sameDay[0], confidence: 'likely', ambiguous: false };
  }
  return null;
}

/** Does the game a coach opened the import from look like the game in the files? */
export function boundGameMismatch(game: any, identity: GameIdentity): string | null {
  if (!game) return null;
  const day = localDay(game.date);
  const dayOff = identity.date && day && Math.abs(dayDiff(day, identity.date)) > 1;
  const oppOff = identity.opponent && game.opponent && !opponentsMatch(game.opponent, identity.opponent);
  if (!dayOff && !oppOff) return null;
  return `These files are ${identity.opponent ? `vs ${identity.opponent}` : 'a game'}${identity.date ? ` on ${prettyDay(identity.date)}` : ''}, but you opened your game vs ${game.opponent || '?'} on ${prettyDay(day)}.`;
}

/** For a CSV with no PDFs and no game picked: the most likely game is the most
 *  recent played game without a box score, preferring one whose score fits. */
export function suggestGameForCsv(rows: ParsedGCPlayer[], games: any[], now = new Date()): any | null {
  const runs = csvRunsScored(rows);
  const played = games
    .filter(g => g && !g.isPractice && g.date && new Date(g.date) <= now)
    .filter(g => !(g.stats && Object.keys(g.stats).length))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  return played.find(g => g.result?.myScore === runs) ?? played[0] ?? null;
}

// ── Roster matching for the CSV ──────────────────────────────────────────────

export interface CsvPlayerRow {
  index: number;
  name: string;                 // "Luke Stoppel"
  number: string;
  played: boolean;
  match: PlayerMatchResult;
  /** Auto-matched with enough confidence that the coach is never asked. */
  sure: boolean;
}

export function matchCsvRows(rows: ParsedGCPlayer[], roster: RosterRef[]): CsvPlayerRow[] {
  const matches = matchPlayers(
    rows.map(r => ({ last: r.last, first: r.first, number: r.number, stats: r.stats, fieldTime: r.fieldTime })),
    roster.map(r => ({ id: r.id, name: r.name, number: r.number })),
  );
  return rows.map((r, i) => ({
    index: i,
    name: `${r.first} ${r.last}`.trim(),
    number: r.number,
    played: !!r.stats,
    match: matches[i],
    sure: matches[i].confidence === 'exact' || matches[i].confidence === 'name',
  }));
}

// ── Does the CSV back up the PDFs? ───────────────────────────────────────────

export interface CsvCheck {
  basis: 'boxScore' | 'scorebook' | 'finalScore' | null;
  compared: number;
  agreed: number;
  /** Names whose lines didn't agree, for the coach to eyeball. */
  off: string[];
  verdict: 'match' | 'mostly' | 'mismatch' | 'unchecked';
}

const lastOf = (n: string) => ((n || '').trim().split(/\s+/).pop() || '').toLowerCase().replace(/…+$/, '');

/**
 * Compare the CSV's per-player lines with the box score (exact: AB, H, R, RBI,
 * BB, SO, and each pitcher's pitches and batters faced), else with the
 * scorebook's play-by-play rollup (PA and hits), else with the game's final
 * score. Verified against Dan's real Pelicans vs Diablos export: all nine
 * batting lines and all three pitching lines agree exactly.
 */
export function checkCsvAgainstPdfs(
  rows: ParsedGCPlayer[],
  resolved: Array<string | null>,                 // playerId per CSV row
  sheetOurs: StatsSheetTeam | undefined,
  recon: ReconciledGame | null,
  knownFinal: { us: number } | undefined,
): CsvCheck {
  const off: string[] = [];
  let compared = 0;
  let agreed = 0;

  if (sheetOurs) {
    rows.forEach((r, i) => {
      const s = r.stats;
      if (!s) return;
      const name = `${r.first} ${r.last}`.trim();
      if ((s.pa ?? 0) > 0 || (s.ab ?? 0) > 0) {
        const b = sheetOurs.batters.find(x => String(x.number).trim() === String(r.number).trim())
          ?? sheetOurs.batters.find(x => lastOf(x.name) && lastOf(x.name) === lastOf(r.last));
        if (b) {
          compared++;
          const same = b.ab === (s.ab ?? 0) && b.h === (s.h ?? 0) && b.r === (s.r ?? 0)
            && b.rbi === (s.rbi ?? 0) && b.bb === (s.bb ?? 0) && b.so === (s.so ?? 0);
          if (same) agreed++; else off.push(name);
        }
      }
      if ((s.pitches ?? 0) > 0) {
        const p = sheetOurs.pitchers.find(x => String(x.number).trim() === String(r.number).trim());
        if (p && p.pitches != null) {
          compared++;
          const same = p.pitches === s.pitches && (p.bf == null || s.bf == null || p.bf === s.bf);
          if (same) agreed++; else if (!off.includes(name)) off.push(name);
        }
      }
    });
    if (compared) return { basis: 'boxScore', compared, agreed, off, verdict: verdictOf(compared, agreed) };
  }

  if (recon && Object.keys(recon.playerGameStats).length) {
    rows.forEach((r, i) => {
      const pid = resolved[i];
      const s = r.stats;
      if (!pid || !s || !((s.pa ?? 0) > 0)) return;
      const line = recon.playerGameStats[pid];
      if (!line) return;
      compared++;
      // The scorebook grid is parsed cell by cell, so allow it one PA of slack.
      if (Math.abs((line.pa ?? 0) - (s.pa ?? 0)) <= 1 && (line.h ?? 0) === (s.h ?? 0)) agreed++;
      else off.push(`${r.first} ${r.last}`.trim());
    });
    if (compared) return { basis: 'scorebook', compared, agreed, off, verdict: verdictOf(compared, agreed) };
  }

  if (knownFinal) {
    const runs = csvRunsScored(rows);
    return { basis: 'finalScore', compared: 1, agreed: runs === knownFinal.us ? 1 : 0, off: [], verdict: runs === knownFinal.us ? 'match' : 'mismatch' };
  }

  return { basis: null, compared: 0, agreed: 0, off: [], verdict: 'unchecked' };
}

function verdictOf(compared: number, agreed: number): CsvCheck['verdict'] {
  if (agreed === compared) return 'match';
  return agreed / compared >= 0.6 ? 'mostly' : 'mismatch';
}

// ── Pitch counts ─────────────────────────────────────────────────────────────

export interface PitchCountPick {
  playerId: string;
  name: string;
  count: number;
  source: 'boxScore' | 'csv' | 'scorebook';
}

/**
 * One pitch count per pitcher, best source first: the box score's P-S (exact),
 * then the CSV's #P (also GameChanger's own count), then the scorebook estimate.
 * reconcile already folded the box score into recon.pitchCounts when it was there.
 */
export function pickPitchCounts(
  recon: ReconciledGame | null,
  sheetOurs: StatsSheetTeam | undefined,
  roster: RosterRef[],
  csvRows: ParsedGCPlayer[] | null,
  resolved: Array<string | null>,
  nameOf: (id: string) => string,
): PitchCountPick[] {
  const out = new Map<string, PitchCountPick>();
  if (recon) {
    for (const [id, count] of Object.entries(recon.pitchCounts)) {
      if (count > 0) out.set(id, { playerId: id, name: nameOf(id), count, source: sheetOurs ? 'boxScore' : 'scorebook' });
    }
  } else if (sheetOurs) {
    // Game stats PDF without a scorebook: same jersey-then-last-name resolution
    // reconcile uses for the box score.
    for (const p of sheetOurs.pitchers) {
      const rp = roster.find(r => String(r.number).trim() === String(p.number).trim())
        ?? roster.find(r => lastOf(r.name) === lastOf(p.name));
      if (rp && (p.pitches ?? 0) > 0) out.set(rp.id, { playerId: rp.id, name: nameOf(rp.id), count: p.pitches!, source: 'boxScore' });
    }
  }
  if (csvRows) {
    csvRows.forEach((r, i) => {
      const id = resolved[i];
      const p = r.stats?.pitches ?? 0;
      if (!id || p <= 0) return;
      const have = out.get(id);
      if (!have || have.source === 'scorebook') out.set(id, { playerId: id, name: nameOf(id), count: p, source: 'csv' });
    });
  }
  return [...out.values()];
}
